import "./BankTransactionsComponent.scss";
import template from "./BankTransactionsComponent.html";
import {IComponentOptions, IOnDestroy} from "angular";
import {Observable, ReplaySubject} from "rxjs";
import {IsoDate} from "../../../pseudo-types/DateTypes";
import {DateTimeUtil} from "../../../js/util/DateTimeUtil";
import moment from "moment";
import {BankAccountTransactionRecord, BankAccountTransactionRecords} from "../../types/BankAccountTransactionRecord";
import {BankAccountTransactionsService} from "../../services/BankAccountTransactionsService";
import {ArrayUtil} from "../../../js/util/ArrayUtil";
import {descendingUsing} from "../../../js/util/UtilFunctions";
import {AccountId} from "../../types/AccountRecord";
import FileSaver from "file-saver";

export const BankTransactions = "tfAccountBankTransactions";

export function bankTransactions(): IComponentOptions {
    return {
        template,
        bindings: {
            accountId: "<"
        },
        controllerAs: "ctrl",
        controller: BankTransactionsComponentController
    };
}

export class BankTransactionsComponentController implements IOnDestroy {

    /** @input */
    public set accountId(accountId: AccountId | null) {
        if (accountId != null) {
            this.accountId$.next(accountId);
        }
    }

    private readonly accountId$ = new ReplaySubject<AccountId>(1);

    public readonly toDate$ = new ReplaySubject<IsoDate>();
    public readonly fromDate$ = new ReplaySubject<IsoDate>();

    public readonly transactions$: Observable<BankAccountTransactionRecords>;
    private readonly export$ = new ReplaySubject<boolean>(1);

    private readonly destroy$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly bankAccountTransactionsService: BankAccountTransactionsService) {
        this.initializeDates();
        this.transactions$ = this.buildTransactions();

        this.export$
            .withLatestFrom(this.transactions$)
            .takeUntil(this.destroy$)
            .subscribe(([_, transactions]) => {
                this.exportCMATransactionsToCsv(transactions, "bank transactions.csv");
            });
    }

    private initializeDates() {
        const toDate = DateTimeUtil.momentToIsoDate(moment());
        this.toDate$.next(toDate);
        const from = moment().subtract(1, "year");
        const fromDate = DateTimeUtil.momentToIsoDate(from);
        this.fromDate$.next(fromDate);
    }

    private buildTransactions(): Observable<BankAccountTransactionRecords> {
        const source$ = Observable.combineLatest(this.accountId$, this.fromDate$, this.toDate$);

        return source$.switchMap(([acc, fromDate, toDate]) => this.retrieveTransactions(acc, fromDate, toDate))
                      .map((transactions) => ArrayUtil.clone(transactions).sort(descendingUsing((a) => a.transactionDate)))
                      .publishReplay(1)
                      .refCount();
    }

    private retrieveTransactions(accountId: AccountId,
                                 fromDate: IsoDate,
                                 toDate: IsoDate): Observable<BankAccountTransactionRecords> {
        const response = this.bankAccountTransactionsService.list(accountId, fromDate, toDate);
        return Observable.fromPromise(response)
                         .catch(() => Observable.empty());
    }

    public updateToDate(date: IsoDate | null) {
        if (date != null) {
            this.toDate$.next(date);
        }
    }

    public updateFromDate(date: IsoDate | null) {
        if (date != null) {
            this.fromDate$.next(date);
        }
    }

    public exportToCsv() {
        this.export$.next(true);
    }

    private exportCMATransactionsToCsv(transactions: BankAccountTransactionRecords, fileName: string) {
        const header = `Date,Settlement Date,Code,Reference,Description,Status,Outstanding,Amount,Balance`;
        const body = transactions.map((model) => this.formatAsCsv(model))
                                 .join("\n");
        const csv = header + "\n" + body;

        const options = {type: "text/csv"};
        const blob = new Blob([csv], options);
        FileSaver.saveAs(blob, fileName);
    }

    private formatAsCsv(transaction: BankAccountTransactionRecord) {
        const transactionDate = transaction.transactionDate;
        const transactionCode = transaction.transactionCode;
        const ref = transaction.transactionReference;
        const description = transaction.transactionDescription;
        const amount = transaction.amount;

        return [transactionDate, transactionCode, ref, description, amount].join(",");
    }

    public $onDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
}
